/* Variant selection.
 *
 * On change we re-render the product section through the Section Rendering API
 * rather than patching price/stock in place — so price, compare-at, inventory
 * line and buy buttons always come from Shopify, never from a client-side
 * guess that can drift.
 */
(function () {
  'use strict';
  const TB = window.TinyBricks || {};

  class Variants extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.url;
      const data = this.querySelector('[data-variant-data]');
      this.variants = data ? JSON.parse(data.textContent) : [];

      this.addEventListener('change', (event) => {
        if (event.target.matches('input[type="radio"]')) this.onChange();
      });

      this.markUnavailable();
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('input[type="radio"]:checked')).map((input) => input.value);
    }

    matchVariant(options) {
      return this.variants.find(function (variant) {
        return options.every(function (value, i) {
          return variant.options[i] === value;
        });
      });
    }

    /* Grey out option values that produce no purchasable variant given the
       other currently-selected options. Nothing is hidden — struck through. */
    markUnavailable() {
      const selected = this.selectedOptions;
      this.querySelectorAll('input[type="radio"]').forEach((input) => {
        const index = parseInt(input.dataset.optionIndex, 10) - 1;
        const probe = selected.slice();
        probe[index] = input.value;
        const match = this.variants.find(function (variant) {
          return probe.every(function (value, i) {
            return variant.options[i] === value;
          });
        });
        const available = !!(match && match.available);
        const label = this.querySelector('label[for="' + input.id + '"]');
        if (label) label.dataset.available = available ? 'true' : 'false';
      });
    }

    onChange() {
      const options = this.selectedOptions;
      const variant = this.matchVariant(options);

      this.querySelectorAll('[data-selected-value]').forEach((el) => {
        const i = parseInt(el.dataset.selectedValue, 10) - 1;
        if (options[i]) el.textContent = options[i];
      });

      this.markUnavailable();

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.updateUrl(variant);
      this.updateFormId(variant);
      document.dispatchEvent(
        new CustomEvent('tb:variant-change', {
          detail: { variant: variant, mediaId: variant.featured_media ? variant.featured_media.id : null }
        })
      );
      this.renderSection(variant);
    }

    setUnavailable() {
      const button = document.querySelector('[data-add-button]');
      if (!button) return;
      button.setAttribute('aria-disabled', 'true');
      button.disabled = true;
      const label = button.querySelector('[data-add-label]');
      if (label) label.textContent = TB.strings.unavailable;
    }

    updateUrl(variant) {
      if (this.dataset.updateUrl !== 'true') return;
      window.history.replaceState({}, '', this.productUrl + '?variant=' + variant.id);
    }

    updateFormId(variant) {
      document.querySelectorAll('[data-variant-id-input]').forEach(function (input) {
        input.value = variant.id;
      });
    }

    renderSection(variant) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const url = this.productUrl + '?variant=' + variant.id + '&section_id=' + this.sectionId;

      fetch(url, { signal: this.controller.signal })
        .then(function (res) { return res.text(); })
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          // Swap only the pieces that depend on the variant. The gallery and
          // the picker itself keep their DOM so focus and scroll survive.
          // The bundle rows price every tier off the variant, so they are
          // re-rendered here too rather than left quoting the old variant.
          [
            '[data-price-block]',
            '[data-inventory-block]',
            '[data-bundle-block]',
            '[data-buy-block]',
            '[data-sku-block]'
          ].forEach(function (selector) {
            const incoming = doc.querySelector(selector);
            const current = document.querySelector(selector);
            if (!incoming || !current) return;
            current.innerHTML = incoming.innerHTML;
            // These wrappers are swapped by selector, so they must survive even
            // when they render nothing — the new variant may need them again.
            // Re-derive `hidden` from the incoming content so an empty one stops
            // taking a row (and its gaps) in the info grid, and a newly filled
            // one comes back.
            current.hidden = incoming.hidden || !current.innerHTML.trim();
          });

          const stickyPrice = doc.querySelector('[data-sticky-price]');
          const currentSticky = document.querySelector('[data-sticky-price]');
          if (stickyPrice && currentSticky) currentSticky.innerHTML = stickyPrice.innerHTML;

          // Anything bound to the swapped markup — the bundle ladder — needs
          // to re-attach itself to the DOM that just replaced it.
          document.dispatchEvent(
            new CustomEvent('tb:section-rendered', { detail: { variant: variant } })
          );
        })
        .catch(function (err) {
          if (err.name !== 'AbortError') console.warn('Variant render failed', err);
        });
    }
  }

  if (!customElements.get('tb-variants')) customElements.define('tb-variants', Variants);
})();
